import React from 'react';
import { Button, Select } from '../../../components/AntComponents';

type Props = {
  currentPage: number;
  totalPages: number | null | undefined;
  totalItems: number | null | undefined;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (n: number) => void;
};

const PaginationControls: React.FC<Props> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange
}) => {
  // 🔒 Normalize so the control ALWAYS shows
  const safeTotalPages = Math.max(1, Number(totalPages ?? 1));
  const safeTotalItems = Math.max(0, Number(totalItems ?? 0));
  const safeCurrentPage = Math.min(Math.max(1, Number(currentPage || 1)), safeTotalPages);

  const itemsPerPageOptions = [
    { value: 10, label: '10 per page' },
    { value: 20, label: '20 per page' },
    { value: 50, label: '50 per page' },
    { value: 100, label: '100 per page' }
  ];

  const startItem = safeTotalItems === 0 ? 0 : (safeCurrentPage - 1) * itemsPerPage + 1;
  const endItem =
    safeTotalItems === 0
      ? 0
      : Math.min(safeCurrentPage * itemsPerPage, safeTotalItems || safeCurrentPage * itemsPerPage);

  const getVisiblePages = () => {
    const delta = 2;
    const range: (number | '...')[] = [];
    const rangeWithDots: (number | '...')[] = [];

    for (
      let i = Math.max(2, safeCurrentPage - delta);
      i <= Math.min(safeTotalPages - 1, safeCurrentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (safeCurrentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (safeCurrentPage + delta < safeTotalPages - 1) {
      rangeWithDots.push('...', safeTotalPages);
    } else if (safeTotalPages > 1) {
      rangeWithDots.push(safeTotalPages);
    }

    // When only 1 page, show just [1]
    if (safeTotalPages === 1) return [1];
    return rangeWithDots;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 pt-6 border-t border-border">
      {/* Items Info */}
      <div className="flex items-center space-x-4">
        <span className="text-sm text-muted-foreground">
          Showing {startItem?.toLocaleString()} to {endItem?.toLocaleString()} of {safeTotalItems?.toLocaleString()} streams
        </span>

        {onItemsPerPageChange && (
          <Select
            options={itemsPerPageOptions}
            value={itemsPerPage}
            onChange={onItemsPerPageChange}
            className="w-32"
          />
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center space-x-2">
        {/* Previous */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          <span className="hidden sm:inline">Previous</span>
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {getVisiblePages().map((page, idx) =>
            page === '...' ? (
              <span key={`dots-${idx}`} className="px-3 py-2 text-muted-foreground">...</span>
            ) : (
              <Button
                key={page}
                variant={safeCurrentPage === page ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onPageChange(page as number)}
                className="w-10 h-10 p-0"
              >
                {page}
              </Button>
            )
          )}
        </div>

        {/* Next */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
          iconName="ChevronRight"
          iconPosition="right"
        >
          <span className="hidden sm:inline">Next</span>
        </Button>
      </div>

      {/* Quick Jump (Desktop Only) */}
      <div className="hidden lg:flex items-center space-x-2">
        <span className="text-sm text-muted-foreground">Go to page:</span>
        <input
          type="number"
          min={1}
          max={safeTotalPages}
          value={safeCurrentPage}
          onChange={(e) => {
            const page = parseInt(e.target.value, 10);
            if (!Number.isNaN(page)) {
              const clamped = Math.min(Math.max(1, page), safeTotalPages);
              onPageChange(clamped);
            }
          }}
          className="w-16 px-2 py-1 text-sm border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          style={{
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-foreground)',
            borderColor: 'var(--color-border)',
          }}
        />
        <span className="text-sm text-muted-foreground">of {safeTotalPages}</span>
      </div>
    </div>
  );
};

export default PaginationControls;
